import React from 'react';
import styled from 'styled-components';

import { Command, State, tabActions } from '@store/index';
import { Label } from '@common/Label';
import { useDispatch, useSelector } from 'react-redux';
import { NodeField, NodeFieldType, NodeInput, NodeState } from '@components/Flow/Nodes';
import { BasicInput } from '@common/Expression/Basic';
import { MultiSelect, Select } from '@common/Select';
import { getValidation } from '@common/Node/utils';
import { useQuery } from '@tanstack/react-query';
import { getModels } from '@requests/telnyx';
import { useTheme } from '../../../../styles';
import { Models, jsonModels } from '@common/Autocomplete';
import { getInferenceProviderColour, isPaidModel, isToolModel } from '@common/Autocomplete/Base';
import { NodeSelector } from '@common/Autocomplete/NodeSelector/NodeSelector';
import { SecurityOutlined } from '@mui/icons-material';
import { TimeZones } from '@common/Autocomplete/TimeZones';
import { customAmdBehaviour, customStreamBehaviour } from '@components/Flow/Nodes/CallControl/Dial/Dial';
import { Toggle } from '../Custom/Toggle';
import { Stack } from '@mui/material';
import { SubText } from '@common/SubText/SubText';
import { getModelKey } from '@common/NodeAuth/ModelAuthKey';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:hover {
    .switch {
      display: block;
    }
  }
`;

interface Props {
  field: NodeField;
  command: Command;
  minRows?: number;
  maxRows?: number;
  override?: {
    title?: string;
    tooltip?: string;
    value?: NodeInput<string> | string;
    update?: (fieldName: 'value' | 'type', value: string | boolean) => void;
    optionType?: boolean;
  };
}

export const NodeFixedInput: React.FC<Props> = ({ field, command, minRows, maxRows, override }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const auth = useSelector((state: State) => state.tabs.active.flow?.authentication);

  const { label, description, placeholder, type } = field;
  const { data: models = [], isLoading: modelsLoading } = useQuery({
    queryKey: ['models'],
    queryFn: getModels,
  });

  const availableModels =
    field.type === NodeFieldType.ModelWithTools ? models.filter((model) => isToolModel(model.id)) : models;

  const state = command.state;
  const value = override?.value || state[field.id as keyof NodeState];
  const isOption = field.type === NodeFieldType.Option || field.type === NodeFieldType.OptionExpression;

  const updateField = (value: string | string[]) => {
    if (typeof override?.update === 'function' && typeof value === 'string') return override.update('value', value);

    const nodeId = command.node.id;
    const fieldId = field.id;

    if (field.validation) value = getValidation(field.validation)(value as string);

    customNodeBehaviour(value);

    dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId, value }));
    dispatch(tabActions.setSaved(false));
  };

  const customFields = ['model', 'elevenlabs_voice_id', 'node_id', 'timezone'];
  /**
   * Used to handle side effects for updating certain state.
   */

  const customNodeBehaviour = (value: string | string[]) => {
    customStreamBehaviour(value, field, command, dispatch);
    customAmdBehaviour(value, field, command, dispatch);
  };

  // shows the auth selected inline with the label if applicable
  const endLabel = () => {
    const modelKey = getModelKey(command.state?.model);
    const isAuthModel = isPaidModel(command.state?.model) && modelKey;
    const modelAuth = isAuthModel ? auth?.[modelKey] : undefined;
    const showModelAuth = field.id === 'model' && isAuthModel;
    if (!showModelAuth) return undefined;

    return {
      label: modelAuth || '',
      icon: (
        <SecurityOutlined
          sx={{
            fontSize: '14px !important',
            color: `${theme.text.standard} !important`,
            paddingRight: '2px',
          }}
        />
      ),
      tooltip:
        'This is the authentication secret that will be used to authenticate your requests to this model. You can change it in the auth tab at the top.',
    };
  };

  return (
    <Container>
      <Label
        title={override?.title || label}
        tooltip={override?.tooltip || description}
        optionAlignment={override?.optionType || isOption}
        endLabel={endLabel()}
      />
      {type === NodeFieldType.Toggle && <Toggle command={command} field={field} hideLabel />}
      {type === NodeFieldType.Input && (
        <BasicInput
          key={`${command.node.id}-${field.id}`}
          keyProp={`${command.node.id}-${field.id}`}
          value={value}
          setValue={(newValue: string) => updateField(newValue)}
          placeholder={placeholder || ''}
          minRows={minRows ? minRows : undefined}
          maxRows={maxRows ? maxRows : undefined}
        />
      )}
      {field.id === 'timezone' && (
        <TimeZones value={value} onChange={(_e, newValue) => updateField(newValue.value)} placeholder='Timezone' />
      )}
      {field.id === 'model' && (
        <Stack spacing={1}>
          <Models
            command={command}
            portal
            value={value}
            loading={modelsLoading}
            labels={availableModels.map((model, index) => ({
              id: index,
              label: model.id.split('/')?.[1],
              value: model.id,
              metadata: {
                organization: model.organization,
                color: getInferenceProviderColour(theme, model.organization),
                tier: model.tier,
                paid: isPaidModel(model.id),
                json: model.owned_by === 'Telnyx' || jsonModels.includes(model.id),
                tools: isToolModel(model.id),
              },
            }))}
            onChange={(e, newValue) => updateField(newValue.value)}
            placeholder='Model'
            showAdditionalToggles={command.config.command_id === 'llm.completion'}
          />
          {field.type === NodeFieldType.ModelWithTools && (
            <SubText>Only models that support tool calling are available.</SubText>
          )}
        </Stack>
      )}
      {field.id === 'node_id' && (
        <NodeSelector
          command={command}
          value={value}
          loading={modelsLoading}
          onChange={(e, newValue) => updateField(newValue.value)}
          placeholder='Jump To Node'
        />
      )}
      {type === NodeFieldType.Option && !field.multiSelect && !customFields.includes(field.id) && (
        <Select
          key={field.id}
          value={value}
          setValue={(newValue) => updateField(newValue)}
          placeholder={placeholder}
          options={
            field?.options || [
              { label: 'true', value: 'true' },
              { label: 'false', value: 'false' },
            ]
          }
        />
      )}
      {type === NodeFieldType.Option && field.multiSelect && !customFields.includes(field.id) && (
        <MultiSelect
          key={field.id}
          value={value}
          setValue={(newValue) => updateField(newValue)}
          placeholder={placeholder}
          options={
            field?.options || [
              { label: 'true', value: 'true' },
              { label: 'false', value: 'false' },
            ]
          }
        />
      )}
    </Container>
  );
};
